import { I1Citycar } from '@e3dc-react/icons'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { Box, Typography } from '@mui/material'
import { isUndefined } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { setActiveInstallationAndStorage } from 'src/Libs/installations/installation.functions'
import { getEmsProductImage } from 'src/Libs/installations/utils/product-image'
import { makeStyles } from 'tss-react/mui'
import HoverCardContainer from '../Utilities/HoverCardContainer'
import { WallboxHardwareDto } from 'src/Libs/wallboxes/dto/wallbox-hardware.dto'
import { ImageVersions } from 'src/Libs/installations/types'
import { Installation } from 'src/Libs/installations/models/installation.model'
import { hexToRgba } from '@e3dc-react/theme'
import { useInstallationAccessType } from 'src/Libs/installations/hooks/use-installation-access-type'
import { getInstallationDashboardUrl } from 'src/Libs/dashboard/routes'

interface StyleProps {
  dashboardEnabled?: boolean
  hasWallbox: boolean
  cardSize: 'small' | 'large'
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  cardContainer: {
    display: 'flex',
    marginTop: theme.spacing(2),
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: props.cardSize === 'small' ? theme.spacing(2) : theme.spacing(4),
    background: theme.palette.background.sidebar,
    width: '100%',
    height: '100%',
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shape.privateInstallationCard.boxShadow,
    cursor: props.dashboardEnabled ? 'pointer' : 'default',
    pointerEvents: props.dashboardEnabled ? 'auto' : 'none',
    position: 'relative',
    overflow: 'hidden',

    '&::after': props.dashboardEnabled
      ? {}
      : {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundColor: hexToRgba(theme.palette.grey.grey02, 0.7),
          borderRadius: 'inherit',
          pointerEvents: 'none',
        },
  },
  leftCardSide: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    margin: 0,
  },
  iconRow: {
    position: 'absolute',
    bottom: props.hasWallbox ? `-${theme.spacing(1)}` : 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  rightCardSide: {
    display: 'flex',
    alignItems: 'end',
    justifyContent: 'end',
    width: '60%',
    [theme.breakpoints.down('sm')]: {
      width: '70%',
    },
  },
  productImg: {
    display: 'block',
    width: 'auto',
    height: 'auto',
    maxWidth: props.cardSize === 'small' ? '65%' : '100%',
    maxHeight: '100%',
    aspectRatio: '1',
  },
  carIcon: {
    color: theme.palette.background.paper,
  },
}))

interface PrivateInstallationCardProps {
  installation: Installation
  cardSize?: 'small' | 'large'
  showSerialNumber?: boolean
}

const PrivateInstallationCard: React.FC<PrivateInstallationCardProps> = ({ installation, cardSize = 'small', showSerialNumber = true }) => {
  const [hasWallbox, setHasWallbox] = useState<boolean>(false)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const installationAccessType = useInstallationAccessType(installation)
  const dashboardEnabled = installationAccessType !== 'access-denied'
  const { classes } = useStyles({ dashboardEnabled, hasWallbox, cardSize })
  const authFetch = useAuthFetchWithResponse()

  const productImage = getEmsProductImage(installation.emsMasterProduct, installation.installationStatus, ImageVersions.ThreeDimensional)

  useEffect(() => {
    if (!isUndefined(installation.emsMasterDeviceId)) {
      authFetch<WallboxHardwareDto>({ path: `wallboxes/${installation.emsMasterDeviceId}` })
        .then(res => (res.data.id.length > 0 ? setHasWallbox(true) : setHasWallbox(false)))
        .catch(err => console.error(err))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installation.emsMasterDeviceId])

  const onClickCard = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
      event.preventDefault()
      if (dashboardEnabled) {
        setActiveInstallationAndStorage(installation, dispatch)
        navigate(getInstallationDashboardUrl(installation))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [installation]
  )

  return (
    <HoverCardContainer className={classes.cardContainer} onClick={onClickCard}>
      {/** left card side */}
      <Box className={classes.leftCardSide}>
        <Box display="flex" flexDirection="column" sx={{ color: 'common.white' }}>
          <Typography variant="h3" color="primary">
            {installation.id}
          </Typography>
          {showSerialNumber && <Typography variant="caption">{installation.emsMasterDeviceId}</Typography>}
          <Typography variant="caption">{installation.emsMasterProduct}</Typography>
        </Box>
        <Box className={classes.iconRow}>{hasWallbox && <I1Citycar className={classes.carIcon} fontSize={'large'} />}</Box>
      </Box>
      {/** right card side */}
      <Box className={classes.rightCardSide}>
        <img src={productImage} alt="ems master product" className={classes.productImg} />
      </Box>
    </HoverCardContainer>
  )
}

export default PrivateInstallationCard
